// Git Checkout Tool - Checks out specific commits for viewing
// Extends WorkspaceTool - has access to appType, fs, and git
// Allows users to view workspace state at specific commits without making changes

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class GitCheckoutTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'gitCheckoutTool';
  }

  /**
   * Checkout a specific commit for viewing
   * @param {string} commitHash - The commit hash to checkout
   * @returns {Promise<Object>} Result with checkout status
   */
  async checkoutCommit(commitHash) {
    try {
      // Route based on app type
      if (this.appType === "browser") {
        return await this.checkoutCommitForBrowser(commitHash);
      }
      if (this.appType === "local") {
        return await this.checkoutCommitForLocal(commitHash);
      }
      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for checkout`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|gitcheckout|error",
          color1: "red",
        },
        "Failed to checkout commit",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Execute git checkout for browser apps using isomorphic-git
   */
  async checkoutCommitForBrowser(commitHash) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    if (!this.git) {
      return this.returnError({}, "Isomorphic-git not available");
    }

    const dir = "/files";

    try {
      // Check if .git exists
      const gitExists = await this.checkGitExists(fs);
      if (!gitExists) {
        return this.returnError(
          {},
          "No git repository found"
        );
      }

      // Verify the commit exists
      try {
        await this.git.resolveRef({
          fs: this.fs,
          dir,
          ref: commitHash,
        });
      } catch (resolveError) {
        return this.returnError(
          {},
          `Commit ${commitHash} not found`
        );
      }

      // Get current HEAD before checkout
      let currentHead = null;
      try {
        currentHead = await this.git.resolveRef({
          fs: this.fs,
          dir,
          ref: "HEAD",
        });
      } catch (headError) {
        // HEAD might not exist for new repos
      }

      logger.log(
        {
          tags: "tools|gitcheckout|info",
          color1: "lime",
        },
        `Checking out commit ${commitHash}, current HEAD: ${currentHead}`
      );

      // Perform the checkout - this puts us in detached HEAD state
      await this.git.checkout({
        fs: this.fs,
        dir,
        ref: commitHash,
        force: true, // Overwrite any working directory changes
      });

      // Get commit details for the UI
      const commitLog = await this.git.log({
        fs: this.fs,
        dir,
        ref: commitHash,
        depth: 1,
      });

      const commitData = commitLog[0]?.commit;
      const commitInfo = {
        hash: commitHash,
        message: (commitData?.message || "").trim(),
        date: commitData?.committer?.timestamp
          ? new Date(commitData.committer.timestamp * 1000).toISOString()
          : null,
        author: commitData?.author?.name || "Unknown",
        email: commitData?.author?.email || "",
      };

      logger.log(
        {
          tags: "tools|gitcheckout|success",
          color1: "lime",
        },
        `Successfully checked out commit ${commitHash}`,
        { commitInfo }
      );

      return this.returnSuccess(
        {
          commitInfo,
          previousHead: currentHead,
          checkoutMode: true,
        },
        `Checked out commit ${commitHash.substring(0, 7)}`
      );
    } catch (error) {
      // Log the full error for debugging
      logger.error(
        {
          tags: "tools|gitcheckout|error",
          color1: "red",
        },
        "Failed to checkout commit",
        {
          commitHash,
          error: error.message,
          stack: error.stack,
          errorName: error.name,
        }
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Exit checkout mode by checking out back to a branch/commit
   * @param {string} targetRef - The ref to checkout back to (e.g., "HEAD", branch name, commit hash)
   * @returns {Promise<Object>} Result with checkout status
   */
  async exitCheckoutMode(targetRef = "HEAD") {
    try {
      if (this.appType === "browser") {
        return await this.exitCheckoutModeForBrowser(targetRef);
      }
      if (this.appType === "local") {
        return await this.exitCheckoutModeForLocal(targetRef);
      }
      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for exit checkout`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|gitcheckout|exit|error",
          color1: "red",
        },
        "Failed to exit checkout mode",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Checkout a commit for `local` apps (real `git checkout` on disk).
   */
  async checkoutCommitForLocal(commitHash) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.gitCheckout) {
      return this.returnError(
        {},
        "Local git checkout requires Revolt Desktop (workspaceLocalAppAPI.gitCheckout)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const res = await api.gitCheckout({
      rootPath: root,
      commitHash,
    });

    if (!res?.success) {
      if (res?.gitNotInstalled) {
        return this.returnError(
          { gitNotInstalled: true },
          res.error ||
            "Git is not installed. Install Git and make sure it's on your PATH to use source control in local workspaces."
        );
      }
      return this.returnError({}, res?.error || "Failed to checkout commit");
    }

    return this.returnSuccess(
      {
        commitInfo: res.commitInfo,
        previousHead: res.previousHead,
        checkoutMode: true,
      },
      `Checked out commit ${String(commitHash).substring(0, 7)}`
    );
  }

  /**
   * Exit checkout mode for browser apps
   */
  async exitCheckoutModeForBrowser(targetRef = "HEAD") {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    if (!this.git) {
      return this.returnError({}, "Isomorphic-git not available");
    }

    const dir = "/files";

    try {
      logger.log(
        {
          tags: "tools|gitcheckout|exit|info",
          color1: "lime",
        },
        `Exiting checkout mode, checking out to ${targetRef}`
      );

      // Checkout back to the target ref
      await this.git.checkout({
        fs: this.fs,
        dir,
        ref: targetRef,
        force: true,
      });

      logger.log(
        {
          tags: "tools|gitcheckout|exit|success",
          color1: "lime",
        },
        `Successfully exited checkout mode, now on ${targetRef}`
      );

      return this.returnSuccess(
        { checkoutMode: false },
        `Exited checkout mode`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|gitcheckout|exit|error",
          color1: "red",
        },
        "Failed to exit checkout mode",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Exit detached checkout for `local` apps.
   */
  async exitCheckoutModeForLocal(targetRef = "HEAD") {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.gitCheckoutExit) {
      return this.returnError(
        {},
        "Local git checkout exit requires Revolt Desktop (workspaceLocalAppAPI.gitCheckoutExit)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const res = await api.gitCheckoutExit({
      rootPath: root,
      targetRef: targetRef || "HEAD",
    });

    if (!res?.success) {
      if (res?.gitNotInstalled) {
        return this.returnError(
          { gitNotInstalled: true },
          res.error ||
            "Git is not installed. Install Git and make sure it's on your PATH to use source control in local workspaces."
        );
      }
      return this.returnError({}, res?.error || "Failed to exit checkout mode");
    }

    return this.returnSuccess({ checkoutMode: false }, "Exited checkout mode");
  }

  /**
   * Check if .git directory exists
   */
  async checkGitExists(fs) {
    try {
      const stats = await fs.stat("/files/.git");
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";
      return isDir;
    } catch (error) {
      return false;
    }
  }
}